/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import React, { useState, useMemo, useCallback } from 'react';

import WidgetConfigurationContext from 'data-lake/preview/contexts/WidgetConfigurationContext';
import type Search from 'views/logic/search/Search';
import type DataLakeWidget from 'data-lake/logic/DataLakeWidget';
import createInitialWidget from 'data-lake/preview/storage-types/internal/createInitialWidget';

type Props = React.PropsWithChildren<{ initialSearch: Search | undefined }>;

const WidgetConfigurationProvider = ({ initialSearch, children = null }: Props) => {
  const [widget, setWidget] = useState<DataLakeWidget>(() => createInitialWidget(initialSearch));

  const resetWidget = useCallback(() => {
    setWidget(createInitialWidget());
  }, []);

  const contextValue = useMemo(
    () => ({
      widget,
      setWidget,
      isLoading: false,
      resetWidget,
    }),
    [resetWidget, widget],
  );

  return <WidgetConfigurationContext.Provider value={contextValue}>{children}</WidgetConfigurationContext.Provider>;
};

export default WidgetConfigurationProvider;
